import React, { useRef, useState } from 'react'
import { Avatar, Button, Input, Spin, Tag } from 'antd'
import { UserOutlined } from '@ant-design/icons'
import { useAddTeamMember, useSearchTeamMemberCandidates } from 'api/Team/Team'
import { TeamMemberCandidate } from 'types/team/team'

interface CoachFormProps {
  teamId: string
  organizationId: string
  onSuccess: () => void
}

const TYPE_COLORS: Record<string, string> = {
  guardian: 'blue', 'authorized-adult': 'cyan', organizer: 'purple', mentor: 'gold', kid: 'green',
}

const CoachForm: React.FC<CoachFormProps> = ({ teamId, organizationId, onSuccess }) => {
  const [query, setQuery] = useState('')
  const [isFocused, setIsFocused] = useState(false)
  const [added, setAdded] = useState<TeamMemberCandidate[]>([])
  const blurTimeout = useRef<any>(null)

  const { data: allResults = [], isFetching } = useSearchTeamMemberCandidates(query, isFocused)
  const { mutate: addMember, isLoading: isAdding } = useAddTeamMember()

  // coaches are organizers or mentors
  const results = allResults.filter(r => r.type === 'organizer' || r.type === 'mentor')

  const handleFocus = () => {
    if (blurTimeout.current) clearTimeout(blurTimeout.current)
    setIsFocused(true)
  }

  const handleBlur = () => {
    blurTimeout.current = setTimeout(() => setIsFocused(false), 150)
  }

  const handleAdd = (candidate: TeamMemberCandidate) => {
    addMember(
      { teamId, payload: { memberIds: [candidate._id], role: 'player', organizationId } },
      {
        onSuccess: () => {
          setAdded(prev => [...prev, candidate])
          setQuery('')
          setIsFocused(false)
          onSuccess()
        },
      },
    )
  }

  return (
    <div>
      <p style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, color: '#000' }}>
        Search coaches by email (organizers &amp; mentors only)
      </p>

      <div style={{ position: 'relative' }}>
        <Input
          value={query}
          onChange={e => setQuery(e.target.value)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder="Click to browse or type to filter…"
          style={{ borderRadius: 12, height: 47 }}
        />

        {isFocused && (
          <div style={{
            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
            background: '#fff', border: '1px solid #e8e8e8',
            borderRadius: 12, boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
            marginTop: 4, maxHeight: 280, overflowY: 'auto',
          }}>
            {isFetching ? (
              <div style={{ padding: 16, textAlign: 'center' }}>
                <Spin size="small" />
              </div>
            ) : results.length === 0 ? (
              <p style={{ color: '#939598', fontSize: 12, padding: '12px 16px', margin: 0 }}>
                {query.length > 0 ? 'No organizers or mentors found.' : 'No coaches found.'}
              </p>
            ) : (
              results.map(candidate => (
                <div
                  key={candidate._id}
                  onMouseDown={e => e.preventDefault()}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '10px 16px', borderBottom: '1px solid #f5f5f5',
                  }}
                >
                  <Avatar src={(candidate.photo as any)?.thumb} icon={<UserOutlined />} size={34} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500, fontSize: 13 }}>{candidate.firstName} {candidate.lastName}</div>
                    <Tag color={TYPE_COLORS[candidate.type] ?? 'default'} style={{ marginTop: 2, fontSize: 11 }}>{candidate.type}</Tag>
                  </div>
                  <Button
                    size="small"
                    loading={isAdding}
                    onClick={() => handleAdd(candidate)}
                    style={{ borderColor: '#2BA7CA', color: '#2BA7CA', borderRadius: 6 }}
                  >
                    Add as Coach
                  </Button>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {added.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Added this session</p>
          {added.map((c, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <Avatar src={(c.photo as any)?.thumb} icon={<UserOutlined />} size={32} />
              <span>{c.firstName} {c.lastName}</span>
              <Tag color={TYPE_COLORS[c.type] ?? 'default'}>{c.type}</Tag>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default CoachForm
